// ============================================================================
// DropdownStore - Persistent Store for Dropdown Data
// ============================================================================
// Manages rarely-changing dropdown data with localStorage caching (24h TTL)
// and real-time Supabase subscriptions. Provides O(1) lookups via Maps.
//
// Usage:
//   state.dropdown.getAssignees()        // Get all assignees
//   state.dropdown.getAssigneeById(id)   // O(1) lookup by ID
//   state.dropdown.subscribe('assignees', callback)  // Listen for changes
// ============================================================================

import { PersistentStore } from './persistent-store.js';
import { globalSubscriptionManager } from './subscription-manager.js';
import { supabase } from 'app/auth/auth.js';
import { getAvailableAssignees } from 'shared/data/profile-api.js';
import { isLeaveApprover, isLeaveApproverOrHr } from 'shared/utils/permissions.js';
import { getCompanyTypes, getServices } from 'shared/data/services-api.js';
import { searchGroups } from 'shared/data/groups-api.js';

const CACHE_VERSION = 15; // Increment to force cache refresh for all users (15: provisional 'Filed' status names say whose payment)

class DropdownStore extends PersistentStore {
    constructor() {
        super({
            storageKey: 'dropdownState',
            cacheVersion: CACHE_VERSION,
            ttl: 24 * 60 * 60 * 1000
        });

        this.data = {
            assignees: [],
            companyTypes: [],
            groups: [],
            services: [],
            statuses: [],
            serviceStatuses: [],
            clientStatuses: [],
            reviewStatuses: []
        };

        // Cached filtered lists (rebuilt when assignees change)
        this._signOffReviewers = [];
        this._budgetReviewers = [];
        this._leaveApprovers = [];
        this._overtimeApprovers = [];
        // Service id -> sorted workflow state[] (one-to-many index, rebuilt
        // alongside the serviceStatuses lookup map). Lets callers grab a
        // service's full state list without iterating the flat array.
        this._statesByService = new Map();
        this._realtimeUnsubs = [];

        // Register lookup maps (primary key, optional secondary key)
        this.registerLookupMap('assignees', a => a.id, a => a.user_id);
        this.registerLookupMap('companyTypes', ct => ct.id, ct => ct.value);
        this.registerLookupMap('groups', g => g.id);
        this.registerLookupMap('services', s => s.id);
        this.registerLookupMap('statuses', s => s.id);
        this.registerLookupMap('serviceStatuses', s => s.id);
        this.registerLookupMap('clientStatuses', s => s.id);
        this.registerLookupMap('clientStatusesByCode', s => s.code);
        this.registerLookupMap('reviewStatuses', s => s.id);
    }

    async _doInit() {
        try {
            const cached = this.loadFromStorage();

            if (cached && !this.shouldRefresh()) {
                this.data = cached.data;
                this.lastFetch = cached.lastFetch;
                this.rebuildAllLookupMaps();
                this.isInitialized = true;
                this._setupRealtimeSubscriptions();
                return;
            }

            await this.fetchAll();
            this.saveToStorage();
            this.isInitialized = true;
            this._setupRealtimeSubscriptions();
        } catch (error) {
            console.error('[DropdownStore] Init failed:', error.message);
            this.isInitialized = true;
            throw error;
        }
    }

    rebuildAllLookupMaps() {
        this.rebuildLookupMap('assignees', this.data.assignees);
        this.rebuildLookupMap('companyTypes', this.data.companyTypes);
        this.rebuildLookupMap('groups', this.data.groups);
        this.rebuildLookupMap('services', this.data.services);
        this.rebuildLookupMap('statuses', this.data.statuses);
        this.rebuildLookupMap('serviceStatuses', this.data.serviceStatuses);
        this.rebuildLookupMap('clientStatuses', this.data.clientStatuses);
        this.rebuildLookupMap('clientStatusesByCode', this.data.clientStatuses);
        this.rebuildLookupMap('reviewStatuses', this.data.reviewStatuses);
        this._rebuildStatesByService();
        this._rebuildReviewerCaches();
    }

    _rebuildStatesByService() {
        this._statesByService.clear();
        for (const s of this.data.serviceStatuses) {
            if (s.service_id == null) continue;
            let bucket = this._statesByService.get(s.service_id);
            if (!bucket) {
                bucket = [];
                this._statesByService.set(s.service_id, bucket);
            }
            bucket.push(s);
        }
        for (const bucket of this._statesByService.values()) {
            bucket.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        }
    }

    _setupRealtimeSubscriptions() {
        if (this._realtimeUnsubs.length > 0) return;

        const makeHandler = (fetchFn, notifyType) => async () => {
            await fetchFn();
            this.saveToStorage();
            this.notifySubscribers(notifyType);
        };

        this._realtimeUnsubs.push(
            globalSubscriptionManager.subscribe('groups', null, makeHandler(() => this.fetchGroups(), 'groups')),
            globalSubscriptionManager.subscribe('profiles', null, makeHandler(() => this.fetchAssignees(), 'assignees')),
            globalSubscriptionManager.subscribe('services', null, makeHandler(() => this.fetchServices(), 'services')),
            globalSubscriptionManager.subscribe('client_statuses', null, makeHandler(() => this.fetchClientStatuses(), 'clientStatuses')),
            // Workflow states change rarely (admin edits) but the cache needs
            // to invalidate when they do, otherwise the task header / status
            // dropdown will lag behind reality until the 24 h TTL expires.
            globalSubscriptionManager.subscribe('service_workflow_states', null, makeHandler(() => this.fetchWorkflowStates(), 'serviceStatuses')),
            // Review statuses are admin-edited in Settings; invalidate the same way.
            globalSubscriptionManager.subscribe('task_review_statuses', null, makeHandler(() => this.fetchReviewStatuses(), 'reviewStatuses'))
        );
    }

    _cleanupRealtimeSubscriptions() {
        this._realtimeUnsubs.forEach(fn => fn());
        this._realtimeUnsubs = [];
    }

    async fetchAll() {
        await Promise.allSettled([
            this.fetchAssignees(),
            this.fetchCompanyTypes(),
            this.fetchGroups(),
            this.fetchServices(),
            this.fetchWorkflowStates(),
            this.fetchClientStatuses(),
            this.fetchReviewStatuses()
        ]);
        this.rebuildAllLookupMaps();
        this.lastFetch = Date.now();
    }

    async fetchAssignees() {
        try {
            const result = await getAvailableAssignees();
            if (result.data) {
                this.data.assignees = result.data.map(p => ({
                    id: p.user_id,
                    user_id: p.user_id,
                    full_name: p.full_name,
                    short_name: p.short_name,
                    email: p.email,
                    avatar_url: p.avatar_url,
                    display_name: p.full_name || p.short_name || 'Unknown',
                    can_sign_off_budgets: p.can_sign_off_budgets || false,
                    can_review_budgets: p.can_review_budgets || false,
                    can_approve_leave: p.can_approve_leave || false,
                    can_manage_leave: p.can_manage_leave || false
                }));
                this.rebuildLookupMap('assignees', this.data.assignees);
                this._rebuildReviewerCaches();
            }
        } catch (error) {
            console.error('[DropdownStore] fetchAssignees failed:', error.message);
        }
    }

    _rebuildReviewerCaches() {
        this._signOffReviewers = this.data.assignees.filter(a => a.can_sign_off_budgets);
        this._budgetReviewers = this.data.assignees.filter(a => a.can_review_budgets);
        this._leaveApprovers = this.data.assignees.filter(isLeaveApprover);
        this._overtimeApprovers = this.data.assignees.filter(isLeaveApproverOrHr);
    }

    /** Users who can approve leave requests (cached, active users only). */
    getLeaveApprovers() { return this._leaveApprovers; }

    /** Users who can approve overtime - leave approvers + leave managers. */
    getOvertimeApprovers() { return this._overtimeApprovers; }

    async fetchCompanyTypes() {
        try {
            const result = await getCompanyTypes();
            if (result.data) {
                this.data.companyTypes = result.data.map(t => ({
                    id: t.id,
                    value: t.value || t.id,
                    label: t.label || t.name,
                    name: t.name || t.label,
                    is_active: t.is_active !== false
                }));
                this.rebuildLookupMap('companyTypes', this.data.companyTypes);
            }
        } catch (error) {
            console.error('[DropdownStore] fetchCompanyTypes failed:', error.message);
        }
    }

    async fetchGroups() {
        try {
            const result = await searchGroups('');
            if (result.data) {
                this.data.groups = result.data.map(g => ({
                    id: g.id,
                    name: g.group_name,
                    group_name: g.group_name,
                    portfolio_manager: g.portfolio_manager,
                    portfolio_manager_name: g.profiles?.full_name || g.profiles?.short_name || null
                }));
                this.rebuildLookupMap('groups', this.data.groups);
            }
        } catch (error) {
            console.error('[DropdownStore] fetchGroups failed:', error.message);
        }
    }

    async fetchServices() {
        try {
            const result = await getServices();
            if (result.data) {
                this.data.services = result.data;
                this.rebuildLookupMap('services', this.data.services);
            }
        } catch (error) {
            console.error('[DropdownStore] fetchServices failed:', error.message);
        }
    }

    /**
     * Fetch the active service_workflow_states ONCE and build both derived
     * slices from the single result:
     *   - `statuses`        compact (id/name/color/categories/sort_order)
     *   - `serviceStatuses` full per-service workflow rows
     * Previously these were two identical round-trips (fetchStatuses via
     * getAllStatuses + fetchServiceStatuses) - the two slowest queries on a cold
     * boot. Same column logic, half the network cost.
     */
    async fetchWorkflowStates() {
        try {
            const { data, error } = await supabase
                .from('service_workflow_states')
                .select('*')
                .eq('is_active', true)
                .order('sort_order');

            if (error) throw error;

            const rows = data || [];

            this.data.statuses = rows.map(s => ({
                id: s.id,
                name: s.display_name,
                color: s.color_hex,
                categories: Array.isArray(s.categories) ? s.categories : [s.categories || 'ACTIVE_WORK'],
                sort_order: s.sort_order
            }));
            this.rebuildLookupMap('statuses', this.data.statuses);

            this.data.serviceStatuses = rows.map(s => ({
                id: s.id,
                service_id: s.service_id,
                name: s.display_name,
                display_name: s.display_name,
                color: s.color_hex,
                color_hex: s.color_hex,
                sort_order: s.sort_order,
                is_initial: s.is_initial,
                is_terminal: s.is_terminal,
                category: s.category,
                linked_source: s.linked_source || null,
                linked_service_id: s.linked_service_id || null,
                requires_review: s.requires_review === true
            }));
            this.rebuildLookupMap('serviceStatuses', this.data.serviceStatuses);
            this._rebuildStatesByService();
        } catch (error) {
            console.error('[DropdownStore] fetchWorkflowStates failed:', error.message);
        }
    }

    /**
     * Fetch the task review statuses (global list, admin-edited in Settings).
     * Category drives behavior: 'pending' (awaiting review), 'sent_back'
     * (returned with comments), 'approved'. Inactive rows are kept so tasks
     * still carrying a deactivated status render its label/colour correctly;
     * getReviewStatuses() (the picker source) returns active ones only.
     */
    async fetchReviewStatuses() {
        try {
            const { data, error } = await supabase
                .from('task_review_statuses')
                .select('*')
                // `id` is the tie-break fn_task_review_defaults uses
                // (ORDER BY sort_order, id). sort_order is NOT NULL DEFAULT 100,
                // so two statuses sharing it is the DEFAULT case, not a rare
                // one - without the second key the client and the database
                // disagree about which status is the opening one.
                .order('sort_order')
                .order('id');

            if (error) throw error;

            this.data.reviewStatuses = data?.map(s => ({
                id: s.id,
                label: s.label,
                color: s.color_hex || '#6B7280',
                color_hex: s.color_hex || '#6B7280',
                category: s.category,
                sort_order: s.sort_order,
                is_active: s.is_active !== false
            })) || [];
            this.rebuildLookupMap('reviewStatuses', this.data.reviewStatuses);
        } catch (error) {
            console.error('[DropdownStore] fetchReviewStatuses failed:', error.message);
        }
    }

    async fetchClientStatuses() {
        try {
            const { data, error } = await supabase
                .from('client_statuses')
                .select('*')
                .eq('is_active', true)
                .order('sort_order');

            if (error) throw error;

            this.data.clientStatuses = data?.map(s => ({
                id: s.id,
                code: s.code,
                label: s.label,
                color: s.color || '#6B7280',
                sort_order: s.sort_order
            })) || [];
            this.rebuildLookupMap('clientStatuses', this.data.clientStatuses);
            this.rebuildLookupMap('clientStatusesByCode', this.data.clientStatuses);
        } catch (error) {
            console.error('[DropdownStore] fetchClientStatuses failed:', error.message);
        }
    }

    // Array getters
    getAssignees() { return this.data.assignees; }
    getCompanyTypes() { return this.data.companyTypes; }
    getGroups() { return this.data.groups; }
    getServiceStatuses() { return this.data.serviceStatuses; }

    /**
     * Cached, sorted workflow states for one service. O(1) Map lookup, no
     * filter pass. Returns an empty array if the service has no states (or
     * the cache is empty - check isInitialized first if you need to know).
     */
    getServiceStatusesByServiceId(serviceId) {
        if (!serviceId) return [];
        return this._statesByService.get(serviceId) || [];
    }
    getClientStatuses() { return this.data.clientStatuses; }
    /** Active review statuses (picker source). Inactive rows stay in the
     *  lookup map so getReviewStatusById still resolves historical values. */
    getReviewStatuses() { return this.data.reviewStatuses.filter(s => s.is_active); }

    getSignOffReviewers() { return this._signOffReviewers; }
    getBudgetReviewers() { return this._budgetReviewers; }

    getServices(companyType = null) {
        if (!companyType) return this.data.services;
        return this.data.services.filter(s =>
            s.service_company_types?.some(sct => sct.company_type_id === companyType)
        );
    }

    getStatuses(type = null) {
        if (!type) return this.data.statuses;
        return this.data.statuses.filter(s => s.type === type);
    }

    // ID lookups (O(1) via lookup maps)
    getAssigneeById(id) { return this.getById('assignees', id); }
    getCompanyTypeById(id) { return this.getById('companyTypes', id); }
    getGroupById(id) { return this.getById('groups', id); }
    getServiceById(id) { return this.getById('services', id); }
    getStatusById(id) { return this.getById('statuses', id); }
    getServiceStatusById(id) { return this.getById('serviceStatuses', id); }
    getClientStatusById(id) { return this.getById('clientStatuses', id); }
    getClientStatusByCode(code) { return this.getById('clientStatusesByCode', code); }
    getReviewStatusById(id) { return this.getById('reviewStatuses', id); }

    /**
     * The review status the DATABASE stamps when a reviewer is first named -
     * the lowest sort_order active `pending` row, mirroring
     * fn_task_review_defaults. It means "a reviewer exists and nothing has
     * happened yet", which is why it reads differently from the other pending
     * statuses: it is the assignee's move, not the reviewer's.
     *
     * Lives here because two places need the same answer (the optimistic echo
     * when a reviewer is assigned, and the Portfolio review filter), and both
     * were deriving it independently off array order.
     * @returns {object|null}
     */
    getOpeningReviewStatus() {
        const pending = this.getReviewStatuses().filter(s => s.category === 'pending');
        if (!pending.length) return null;
        // Same ordering as fn_task_review_defaults: sort_order, then id. The
        // strict `<` keeps the earlier row on a tie, and the fetch above orders
        // by id, so "earlier" means the lower id - the row Postgres picks.
        return pending.reduce((lowest, s) => {
            const a = Number(s.sort_order) || 0;
            const b = Number(lowest.sort_order) || 0;
            if (a !== b) return a < b ? s : lowest;
            return Number(s.id) < Number(lowest.id) ? s : lowest;
        });
    }

    async refresh() {
        await this.fetchAll();
        this.saveToStorage();
        this.notifySubscribers();
    }

    /**
     * Refresh just the workflow states slice (cheaper than refresh()).
     * Called by the workflow state editors after a save so changes apply
     * immediately without waiting for the 24h TTL.
     */
    async refreshServiceStatuses() {
        await this.fetchWorkflowStates();
        this.saveToStorage();
        this.notifySubscribers('serviceStatuses');
    }

    /**
     * Refresh just the review statuses slice. Called by the Settings review
     * statuses editor after a save so changes apply immediately.
     */
    async refreshReviewStatuses() {
        await this.fetchReviewStatuses();
        this.saveToStorage();
        this.notifySubscribers('reviewStatuses');
    }

    clear() {
        this.data = {
            assignees: [],
            companyTypes: [],
            groups: [],
            services: [],
            statuses: [],
            serviceStatuses: [],
            clientStatuses: [],
            reviewStatuses: []
        };
        this._signOffReviewers = [];
        this._budgetReviewers = [];
        this._leaveApprovers = [];
        this._overtimeApprovers = [];
        this.clearStorage();
        this._cleanupRealtimeSubscriptions();
        // Reset init state so init() can be called again after clear()
        this.isInitialized = false;
        this.initPromise = null;
    }

    destroy() {
        this._cleanupRealtimeSubscriptions();
        super.destroy();
    }
}

export { DropdownStore };

if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    window.DropdownStore = DropdownStore;
}
